import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Input, InputNumber, Select, Tag, Tooltip } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  CloseOutlined,
  BookOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import {
  OPEN_TEXT_LENGTH,
  QUESTION_TYPE,
  QUESTION_TYPE_META,
  QUESTION_TYPE_OPTIONS,
  RATING_SCALE_META,
} from "./constants";
import { videoDurationLimits } from "./surveyWidgetFlow";
import { AGENT_NOTE_MAX, QUESTION_TOPIC_MAX } from "./surveyApiHelpers";
import useKnowledgeTopics from "pages/FeedbackCapture/settings/useKnowledgeTopics";
import TopicSelect from "pages/FeedbackCapture/settings/TopicSelect";
import styles from "./style.module.scss";

const { TextArea } = Input;

// A single editable question row inside the survey builder. Fully controlled:
// every change is bubbled up via onChange so the drawer owns the questions array.
const QuestionEditor = ({
  question,
  index,
  total,
  isActive = false,
  onFocus,
  error,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  // Video answers are offered only while they are on for the organisation.
  videoEnabled = false,
  // A voice survey's question can carry a note for the AI agent.
  voice = false,
  // The company question bank: how many surveys use this question's bank question, and
  // (for those who may) adding an unlinked question to the bank.
  bankUsage = 0,
  onAddToBank,
}) => {
  const { t } = useTranslation();
  const topics = useKnowledgeTopics(voice);
  // A question linked to the bank takes its wording there; the survey keeps only required
  // and the agent note, unless it unlinks the question to edit it for itself.
  const locked = !!question.bankId;
  const meta = QUESTION_TYPE_META[question.type] || {};

  const patch = (changes) => onChange({ ...question, ...changes });

  const handleTypeChange = (type) => {
    const nextMeta = QUESTION_TYPE_META[type] || {};
    const changes = { type, answerLength: undefined };
    // Seed a couple of empty options when switching into a choice type.
    if (nextMeta.hasOptions && (!question.options || question.options.length === 0)) {
      changes.options = ["", ""];
    }
    patch(changes);
  };

  const handleOptionChange = (optIndex, value) => {
    const options = [...(question.options || [])];
    options[optIndex] = value;
    patch({ options });
  };

  const addOption = () => patch({ options: [...(question.options || []), ""] });

  const removeOption = (optIndex) =>
    patch({
      options: (question.options || []).filter((_, i) => i !== optIndex),
    });

  return (
    <div
      className={[
        styles.questionCard,
        isActive ? styles.questionCardActive : "",
        error ? styles.questionCardError : "",
      ]
        .filter(Boolean)
        .join(" ")}
      tabIndex={0}
      onFocus={onFocus}
      onMouseDown={onFocus}
    >
      <div className={styles.questionCardHeader}>
        <span className={styles.questionIndex}>Q{index + 1}</span>
        <Select
          size="small"
          value={question.type}
          onChange={handleTypeChange}
          disabled={locked}
          options={QUESTION_TYPE_OPTIONS.filter(
            (o) => o.value !== QUESTION_TYPE.VIDEO || videoEnabled || question.type === QUESTION_TYPE.VIDEO
          ).map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
          style={{ width: 170 }}
        />
        {meta.hasScale && (
          <Select
            size="small"
            value={question.ratingScale}
            onChange={(ratingScale) => patch({ ratingScale })}
            disabled={locked}
            options={Object.entries(RATING_SCALE_META).map(([value, m]) => ({
              value,
              label: t(m.labelKey),
            }))}
            style={{ width: 140 }}
          />
        )}
        {question.type === QUESTION_TYPE.OPEN_TEXT && (
          <Select
            size="small"
            value={question.answerLength === OPEN_TEXT_LENGTH.SHORT ? OPEN_TEXT_LENGTH.SHORT : OPEN_TEXT_LENGTH.LONG}
            disabled={locked}
            onChange={(answerLength) =>
              patch({ answerLength: answerLength === OPEN_TEXT_LENGTH.SHORT ? answerLength : undefined })
            }
            options={[
              { value: OPEN_TEXT_LENGTH.LONG, label: t("surveyManagement.answer_length_long") },
              { value: OPEN_TEXT_LENGTH.SHORT, label: t("surveyManagement.answer_length_short") },
            ]}
            style={{ width: 140 }}
          />
        )}
        <div className={styles.questionCardActions}>
          <Tooltip title={t("surveyManagement.move_up")}>
            <Button
              size="small"
              type="text"
              icon={<ArrowUpOutlined />}
              disabled={index === 0}
              onClick={onMoveUp}
            />
          </Tooltip>
          <Tooltip title={t("surveyManagement.move_down")}>
            <Button
              size="small"
              type="text"
              icon={<ArrowDownOutlined />}
              disabled={index === total - 1}
              onClick={onMoveDown}
            />
          </Tooltip>
          <Tooltip title={t("surveyManagement.remove_question")}>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={onRemove}
            />
          </Tooltip>
        </div>
      </div>

      {locked ? (
        <div className={styles.bankStrip}>
          <Tag color="cyan" icon={<LinkOutlined />} className={styles.bankTag}>
            {t("surveyManagement.bank.linked")}
          </Tag>
          <span className={styles.fieldHint}>{t("surveyManagement.bank.linked_hint", { count: bankUsage })}</span>
          <Button size="small" type="link" onClick={() => patch({ bankId: undefined })}>
            {t("surveyManagement.bank.unlink")}
          </Button>
        </div>
      ) : onAddToBank && (question.text || "").trim() ? (
        <div className={styles.bankStrip}>
          <Button size="small" type="link" icon={<BookOutlined />} onClick={() => onAddToBank(question)}>
            {t("surveyManagement.bank.add_to_bank")}
          </Button>
        </div>
      ) : null}

      <TextArea
        value={question.text}
        disabled={locked}
        onChange={(e) => patch({ text: e.target.value })}
        placeholder={t("surveyManagement.question_text_placeholder")}
        autoSize={{ minRows: 1, maxRows: 3 }}
        className={styles.questionText}
      />

      {question.type === QUESTION_TYPE.URL && (
        <div className={styles.urlSettings}>
          <label className={styles.videoSetting}>
            <span>{t("surveyManagement.url_domain_label")}</span>
            <Input
              size="small"
              value={question.urlDomain || ""}
              disabled={locked}
              onChange={(e) => patch({ urlDomain: e.target.value })}
              placeholder="bigfive-test.com"
              maxLength={253}
              style={{ width: 220 }}
            />
          </label>
          <span className={styles.fieldHint}>{t("surveyManagement.url_domain_hint")}</span>
        </div>
      )}

      {question.type === QUESTION_TYPE.VIDEO && (
        <div className={styles.videoSettings}>
          <label className={styles.videoSetting}>
            <span>{t("surveyManagement.video_min_seconds")}</span>
            <InputNumber
              size="small"
              min={0}
              max={600}
              value={videoDurationLimits(question).min}
              disabled={locked}
              onChange={(videoMinSeconds) => patch({ videoMinSeconds })}
            />
          </label>
          <label className={styles.videoSetting}>
            <span>{t("surveyManagement.video_max_seconds")}</span>
            <InputNumber
              size="small"
              min={1}
              max={600}
              value={videoDurationLimits(question).max}
              disabled={locked}
              onChange={(videoMaxSeconds) => patch({ videoMaxSeconds })}
            />
          </label>
          <TextArea
            value={question.videoInstructions || ""}
            disabled={locked}
            onChange={(e) => patch({ videoInstructions: e.target.value })}
            placeholder={t("surveyManagement.video_instructions_placeholder")}
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={1500}
          />
        </div>
      )}

      {meta.hasOptions && (
        <div className={styles.optionsList}>
          {(question.options || []).map((opt, optIndex) => (
            <div className={styles.optionRow} key={optIndex}>
              <span className={styles.optionBullet}>
                {question.type === QUESTION_TYPE.MULTIPLE_CHOICE ? "☐" : "◯"}
              </span>
              <Input
                size="small"
                value={opt}
                disabled={locked}
                onChange={(e) => handleOptionChange(optIndex, e.target.value)}
                placeholder={`${t("surveyManagement.option")} ${optIndex + 1}`}
              />
              <Button
                size="small"
                type="text"
                icon={<CloseOutlined />}
                onClick={() => removeOption(optIndex)}
                disabled={locked || (question.options || []).length <= 1}
              />
            </div>
          ))}
          {locked ? null : (
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addOption}
              className={styles.addOptionBtn}
            >
              {t("surveyManagement.add_option")}
            </Button>
          )}
        </div>
      )}

      {voice ? (
        <div className={styles.agentNote}>
          <label className={styles.fieldLabel}>{t("surveyManagement.question_topic")}</label>
          <TopicSelect
            value={question.topic || ""}
            onChange={(topic) => patch({ topic })}
            topics={topics}
            disabled={locked}
            placeholder={t("surveyManagement.question_topic_ph")}
            maxLength={QUESTION_TOPIC_MAX}
            allowClear
            className={styles.topicPicker}
          />
          <p className={styles.fieldHint}>{t("surveyManagement.question_topic_hint")}</p>
        </div>
      ) : null}

      {voice ? (
        <div className={styles.agentNote}>
          <label className={styles.fieldLabel}>{t("surveyManagement.agent_note")}</label>
          <TextArea
            value={question.agentNote || ""}
            onChange={(e) => patch({ agentNote: e.target.value })}
            placeholder={t("surveyManagement.agent_note_ph")}
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={AGENT_NOTE_MAX}
          />
          <p className={styles.fieldHint}>{t("surveyManagement.agent_note_hint")}</p>
        </div>
      ) : null}

      <div className={styles.questionCardFooter}>
        <Checkbox
          checked={question.required}
          onChange={(e) => patch({ required: e.target.checked })}
        >
          {t("surveyManagement.required")}
        </Checkbox>
        {error && <span className={styles.questionError}>{error}</span>}
      </div>
    </div>
  );
};

export default QuestionEditor;
